import { ChangeDetectorRef, Component, DestroyRef, HostBinding, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivationEnd } from '@angular/router';

import { EpcotConfig, LiveSectionConfig, ScrollingSizeConfig, SkeletonConfig } from '@frontend/sports/common/client-config-data-access';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { ScrollContainerService } from '@frontend/sports/common/core/utils/scroll-container';
import { ThemeInverse } from '@frontend/sports/common/ui/shared';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { DeviceService } from '@frontend/vanilla/core';

import { HideHeaderService } from '../common/hide-header.service';
import { NavigationRouterService } from '../navigation/navigation-router.service';
import { PopupManager } from '../popup/popup-manager.service';
import { SkeletonPage } from '../skeleton-ui/skeleton-page.model';
import { SkeletonService } from '../skeleton-ui/skeleton.service';
import { TabBarItem } from '../tab-bar/tab-bar.models';
import { LiveEventListService, LiveSportOverviewModel, RegionBarItem } from './live-event-list.service';
import { LiveHistoryService } from './live-history.service';
import { LiveSportNavigationService } from './live-sport-navigation.service';

@Component({
    selector: 'ms-live',
    templateUrl: 'live.component.html',
    styleUrls: ['live.component.scss'],
    standalone: false,
})
export class LiveComponent implements OnInit, OnDestroy {
    @HostBinding('class') className = 'live-list';

    regions = signal<RegionBarItem[]>([]);
    selectedRegion = computed(() => this.regions().find((r) => r.active) ?? null);

    hideFilters = false;
    skeletonPage: SkeletonPage | undefined;
    readonly isEpcotEnabled: boolean;
    readonly isMobileMode: boolean;
    themeInverseIndicator: ThemeInverse;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private routerHelper: RouterEventsService,
        private navigationRouter: NavigationRouterService,
        private liveHelper: LiveEventListService,
        private liveHistory: LiveHistoryService,
        public scrollConfig: ScrollingSizeConfig,
        private popupManager: PopupManager,
        private liveConfig: LiveSectionConfig,
        public device: DeviceService,
        private changeRef: ChangeDetectorRef,
        private skeletonService: SkeletonService,
        public skeletonConfig: SkeletonConfig,
        private liveSportNavigationService: LiveSportNavigationService,
        private scrollContainer: ScrollContainerService,
        private hideHeaderService: HideHeaderService,
        private epcotConfig: EpcotConfig,
        private redirect: RedirectHelperService,
    ) {
        if (skeletonConfig.isEnabled && !device.isRobot) {
            this.skeletonService.skeletonPage$?.pipe(takeUntilDestroyed()).subscribe((page: SkeletonPage | undefined) => {
                if (page === SkeletonPage.Live || page === undefined) {
                    this.skeletonPage = page;
                }

                this.changeRef.markForCheck();
            });
        }
        if (this.scrollConfig.hideHeaderOnScroll.length > 0) {
            this.scrollContainer.scroll.pipe(takeUntilDestroyed()).subscribe(() => {
                this.hideHeaderService.onScroll();
            });
        }
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
        this.isMobileMode = this.device.isMobile;
        this.themeInverseIndicator = this.isMobileMode
            ? {
                  'mgm-4': { isInverse: true },
              }
            : {};
    }

    ngOnInit(): void {
        this.routerHelper.currentActivationEnd.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
            const isAzMenuOpened = this.popupManager.isPopupOpen('sports');
            if (!isAzMenuOpened) {
                this.setNavigation(event);
            }
        });
    }

    ngOnDestroy(): void {
        this.liveSportNavigationService.detach();
        this.navigationRouter.resetNavigation();
        this.hideHeaderService.removeHideHeader();
    }

    onSelected(event: Event, selected: TabBarItem) {
        this.regions.update((nav) =>
            nav.map((item) => ({
                ...item,
                active: item.id === selected.id,
            })),
        );
        this.redirect.goToPage(selected.url, selected.ignoreBrowserHistory);
        return false;
    }

    private setNavigation(data: ActivationEnd | undefined): void {
        this.navigationRouter.setNavigation('live');

        if (!data || !data.snapshot.data.model || data.snapshot.data.tag === RouteTag.LiveLeague) {
            return;
        }

        this.liveHistory.saveRouteData(data);

        const routeData = data.snapshot.data.model;
        if (routeData.sports) {
            if (routeData.regions) {
                this.setRegionNavigation(routeData);
            } else {
                this.regions.set([]);
            }
        } else {
            this.regions.set([]);
        }
        if (routeData.selectedSportId) {
            this.hideFilters = this.liveConfig.hideFiltersForSportIds.includes(routeData.selectedSportId);
        }
    }

    private setRegionNavigation(model: LiveSportOverviewModel): void {
        const sport = model.sports.find((x) => x.id === model.selectedSportId);

        if (sport) {
            this.regions.set(this.liveHelper.getRegionTabs(model.regions, sport, model.selectedRegionId));
        }
    }
}
